import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface Note {
  id: number;
  note_id: string;
  user_id: number;
  title: string;
  blocks: Array<{
    block_id: string;
    order: number;
  }>;
  properties?: {
    [key: string]: any;
  };
  type?: string;
  created_at: string;
  updated_at: string;
}

export interface NoteWithBlocks extends Note {
  blocks: Array<{
    block_id: string;
    order: number;
    content?: {
      block_id: string;
      content_type: string;
      text_value: string;
      created_at: string;
      updated_at: string;
    } | null;
  }>;
}

export interface CreateNoteData {
  user_id: number;
  title: string;
  blocks: Array<{
    block_id: string;
    order: number;
  }>;
  properties?: {
    [key: string]: any;
  };
  note_id?: string; // Optional, will be generated from title if not provided
  type?: string; // Optional, type of note (e.g., 'place', 'habit', etc.)
}

export interface UpdateNoteData {
  title: string;
  blocks: Array<{
    block_id: string;
    order: number;
  }>;
  properties?: {
    [key: string]: any;
  };
  type?: string; // Optional, type of note (e.g., 'place', 'habit', etc.)
}

export interface GetUserNotesParams {
  start_date?: string; // YYYY-MM-DD format
  end_date?: string; // YYYY-MM-DD format
  page?: number;
  limit?: number;
  sort_order?: 'ASC' | 'DESC';
  type?: string; // Filter by note type (e.g., 'place')
}

export interface SearchNotesParams {
  user_id: number;
  query: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface GenerateNoteIdParams {
  title?: string;
  note_id?: string;
}

export interface GenerateNoteIdResponse {
  original: string;
  sanitized_id: string;
  is_available: boolean;
}

export interface PaginationResponse {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface NotesResponse {
  success: boolean;
  data: Note[];
  pagination?: PaginationResponse;
  search_query?: string;
}

export interface SingleNoteResponse {
  success: boolean;
  data: Note;
  message?: string;
}

export interface NoteWithBlocksResponse {
  success: boolean;
  data: NoteWithBlocks;
}

export class NotesService {
  /**
   * Create a new note
   */
  static async createNote(noteData: CreateNoteData): Promise<Note> {
    const response = await makeAuthenticatedRequest('/notes', {
      method: 'POST',
      body: JSON.stringify(noteData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create note');
    }

    return response.data;
  }

  /**
   * Get a note by note_id (requires user_id for security)
   */
  static async getNoteById(noteId: string, userId: number): Promise<Note> {
    const response = await makeAuthenticatedRequest(`/notes/${noteId}?user_id=${userId}`);

    if (!response.success) {
      throw new Error('Failed to fetch note');
    }

    return response.data;
  }

  /**
   * Get a note with its associated blocks (requires user_id for security)
   */
  static async getNoteWithBlocks(noteId: string, userId: number): Promise<NoteWithBlocks> {
    const response = await makeAuthenticatedRequest(`/notes/${noteId}/with-blocks?user_id=${userId}`);

    if (!response.success) {
      throw new Error('Failed to fetch note with blocks');
    }

    return response.data;
  }

  /**
   * Get user's notes with optional filtering and pagination
   */
  static async getUserNotes(userId: number, params: GetUserNotesParams = {}): Promise<NotesResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params.type) queryParams.append('type', params.type);

    const url = `/notes/user/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch user notes');
    }

    return response;
  }

  /**
   * Update a note
   */
  static async updateNote(noteId: string, updateData: UpdateNoteData): Promise<Note> {
    const response = await makeAuthenticatedRequest(`/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update note');
    }

    return response.data;
  }

  /**
   * Delete a note
   */
  static async deleteNote(noteId: string): Promise<void> {
    const response = await makeAuthenticatedRequest(`/notes/${noteId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete note');
    }
  }

  /**
   * Search notes by content in blocks
   */
  static async searchNotes(params: SearchNotesParams): Promise<NotesResponse> {
    const queryParams = new URLSearchParams();
    
    queryParams.append('user_id', params.user_id.toString());
    queryParams.append('query', params.query);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await makeAuthenticatedRequest(`/notes/search?${queryParams.toString()}`);

    if (!response.success) {
      throw new Error('Failed to search notes');
    }

    return response;
  }

  /**
   * Generate a sanitized note_id from a title or existing note_id
   */
  static async generateNoteId(params: GenerateNoteIdParams): Promise<GenerateNoteIdResponse> {
    const response = await makeAuthenticatedRequest('/notes/generate-id', {
      method: 'POST',
      body: JSON.stringify(params)
    });

    if (!response.success) {
      throw new Error('Failed to generate note ID');
    }

    return response.data;
  }

  /**
   * Create a note with auto-generated ID from title
   */
  static async createNoteFromTitle(userId: number, title: string, blocks: Array<{block_id: string; order: number}>, properties?: any): Promise<Note> {
    return await this.createNote({
      user_id: userId,
      title,
      blocks,
      properties
    });
  }

  /**
   * Create a note with specific note_id
   */
  static async createNoteWithId(userId: number, noteId: string, title: string, blocks: Array<{block_id: string; order: number}>, properties?: any): Promise<Note> {
    return await this.createNote({
      user_id: userId,
      note_id: noteId,
      title,
      blocks,
      properties
    });
  }

  /**
   * Get notes for a specific date range
   */
  static async getNotesInRange(userId: number, startDate: string, endDate: string): Promise<Note[]> {
    const response = await this.getUserNotes(userId, {
      start_date: startDate,
      end_date: endDate,
      limit: 100, // Get up to 100 notes in range
      sort_order: 'DESC'
    });

    return response.data;
  }

  /**
   * Get recent notes for a user
   */
  static async getRecentNotes(userId: number, limit: number = 10): Promise<Note[]> {
    const response = await this.getUserNotes(userId, {
      limit,
      sort_order: 'DESC'
    });

    return response.data;
  }

  /**
   * Check if a note exists by note_id (requires user_id for security)
   */
  static async noteExists(noteId: string, userId: number): Promise<boolean> {
    try {
      await this.getNoteById(noteId, userId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get notes by property value (searches in properties)
   */
  static async getNotesByProperty(userId: number, propertyKey: string, propertyValue: any): Promise<Note[]> {
    // This would require a more complex search implementation
    // For now, we'll get all user notes and filter by property
    const response = await this.getUserNotes(userId, { limit: 1000 });
    
    return response.data.filter(note => {
      if (!note.properties) return false;
      return note.properties[propertyKey] === propertyValue;
    });
  }

  /**
   * Get notes by type
   */
  static async getNotesByType(userId: number, type: string, params: GetUserNotesParams = {}): Promise<NotesResponse> {
    return await this.getUserNotes(userId, {
      ...params,
      type
    });
  }

  /**
   * Update note properties only (requires user_id for security)
   */
  static async updateNoteProperties(noteId: string, userId: number, properties: any): Promise<Note> {
    // First get the current note
    const currentNote = await this.getNoteById(noteId, userId);
    
    // Update with new properties
    return await this.updateNote(noteId, {
      title: currentNote.title,
      blocks: currentNote.blocks,
      properties
    });
  }

  /**
   * Add property to a note (requires user_id for security)
   */
  static async addPropertyToNote(noteId: string, userId: number, propertyKey: string, propertyValue: any): Promise<Note> {
    const currentNote = await this.getNoteById(noteId, userId);
    
    return await this.updateNoteProperties(noteId, userId, {
      ...currentNote.properties,
      [propertyKey]: propertyValue
    });
  }

  /**
   * Remove property from a note (requires user_id for security)
   */
  static async removePropertyFromNote(noteId: string, userId: number, propertyKey: string): Promise<Note> {
    const currentNote = await this.getNoteById(noteId, userId);
    const newProperties = { ...currentNote.properties };
    delete newProperties[propertyKey];
    
    return await this.updateNoteProperties(noteId, userId, newProperties);
  }
}

export default NotesService;
